import { useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Input,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Label,
  Skeleton,
  Separator,
  toast,
} from '@ponzinomics/ui';
import type { ApiKeyEnvironment } from '@ponzinomics/types';
import { useApiKeys, useCreateApiKey, useRevokeApiKey } from '../hooks/use-api-keys';
import { useCurrentProject } from '../hooks/use-current-project';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function timeAgo(iso: string): string {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function SettingsPage(): React.ReactElement {
  const { projectId, project } = useCurrentProject();
  const { data: apiKeys, isLoading } = useApiKeys();
  const createApiKey = useCreateApiKey();
  const revokeApiKey = useRevokeApiKey();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showRevokeDialog, setShowRevokeDialog] = useState<string | null>(null);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Create form state
  const [keyName, setKeyName] = useState('');
  const [keyEnvironment, setKeyEnvironment] = useState<ApiKeyEnvironment>('test');

  const activeKeys = (apiKeys ?? []).filter((k) => !k.revokedAt);
  const revokedKeys = (apiKeys ?? []).filter((k) => k.revokedAt);

  const handleCreate = async (): Promise<void> => {
    if (!keyName.trim()) return;
    try {
      const result = await createApiKey.mutateAsync({
        name: keyName.trim(),
        environment: keyEnvironment,
      });
      setNewlyCreatedKey(result.key);
      setKeyName('');
      setKeyEnvironment('test');
      setShowCreateDialog(false);
      toast({ title: 'API key created', description: 'Copy the key before dismissing.' });
    } catch {
      toast({ title: 'Failed to create API key', variant: 'destructive' });
    }
  };

  const handleRevoke = async (keyId: string): Promise<void> => {
    try {
      await revokeApiKey.mutateAsync(keyId);
      setShowRevokeDialog(null);
      toast({ title: 'API key revoked' });
    } catch {
      toast({ title: 'Failed to revoke API key', variant: 'destructive' });
    }
  };

  const handleCopyKey = async (): Promise<void> => {
    if (!newlyCreatedKey) return;
    await navigator.clipboard.writeText(newlyCreatedKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!projectId) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Settings</h1>
          <p className="mt-1 text-gray-400">Select a project first to manage settings.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Settings</h1>
        <p className="mt-1 text-gray-400">
          Manage API keys and project configuration for{' '}
          <span className="text-white font-medium">{project?.name ?? 'your project'}</span>
        </p>
      </div>

      {/* New Key Alert */}
      {newlyCreatedKey && (
        <Card className="border-brand-400/30 bg-brand-400/5">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-brand-400">
                  API key created successfully
                </p>
                <p className="mt-1 text-sm text-gray-400">
                  Copy this key now. You won't be able to see it again.
                </p>
                <code className="mt-3 block rounded-md bg-gray-950 border border-white/[0.08] px-4 py-3 text-sm text-white font-mono break-all">
                  {newlyCreatedKey}
                </code>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyKey}
                >
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setNewlyCreatedKey(null)}
                >
                  Dismiss
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* API Keys Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>API Keys</CardTitle>
              <CardDescription>
                Manage API keys for accessing Ponzinomics features programmatically.
              </CardDescription>
            </div>
            <Button onClick={() => setShowCreateDialog(true)}>
              Create Key
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : activeKeys.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-gray-400">No API keys yet.</p>
              <p className="mt-1 text-sm text-gray-500">
                Create your first API key to start integrating.
              </p>
              <Button
                className="mt-4"
                variant="outline"
                onClick={() => setShowCreateDialog(true)}
              >
                Create API Key
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Key</TableHead>
                  <TableHead>Environment</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Last Used</TableHead>
                  <TableHead className="text-right">Usage</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeKeys.map((key) => (
                  <TableRow key={key.id}>
                    <TableCell className="font-medium text-white">
                      {key.name}
                    </TableCell>
                    <TableCell>
                      <code className="text-sm text-gray-400">
                        {key.keyPrefix}****{key.lastFour}
                      </code>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={key.environment === 'live' ? 'default' : 'secondary'}
                      >
                        {key.environment}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-gray-400">
                      {formatDate(key.createdAt)}
                    </TableCell>
                    <TableCell className="text-gray-400">
                      {key.lastUsedAt ? timeAgo(key.lastUsedAt) : 'Never'}
                    </TableCell>
                    <TableCell className="text-right text-gray-400">
                      {key.usageCount.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        onClick={() => setShowRevokeDialog(key.id)}
                      >
                        Revoke
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* Revoked keys section */}
          {revokedKeys.length > 0 && (
            <>
              <Separator className="my-6" />
              <div>
                <h3 className="text-sm font-medium text-gray-400 mb-3">
                  Revoked Keys ({revokedKeys.length})
                </h3>
                <Table>
                  <TableBody>
                    {revokedKeys.map((key) => (
                      <TableRow key={key.id} className="opacity-50">
                        <TableCell className="text-white">{key.name}</TableCell>
                        <TableCell>
                          <code className="text-sm text-gray-500">
                            {key.keyPrefix}****{key.lastFour}
                          </code>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">revoked</Badge>
                        </TableCell>
                        <TableCell className="text-gray-500">
                          {formatDate(key.createdAt)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Create Key Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create API Key</DialogTitle>
            <DialogDescription>
              Create a new API key for accessing Ponzinomics features.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="key-name">Key Name</Label>
              <Input
                id="key-name"
                placeholder="e.g. Production Backend"
                value={keyName}
                onChange={(e) => setKeyName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="key-env">Environment</Label>
              <Select
                value={keyEnvironment}
                onValueChange={(v) => setKeyEnvironment(v as ApiKeyEnvironment)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="test">Test</SelectItem>
                  <SelectItem value="live">Live</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                Test keys work only in development. Use live keys for production.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!keyName.trim() || createApiKey.isPending}
            >
              {createApiKey.isPending ? 'Creating...' : 'Create Key'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke Confirmation Dialog */}
      <Dialog
        open={showRevokeDialog !== null}
        onOpenChange={(open) => {
          if (!open) setShowRevokeDialog(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke API Key</DialogTitle>
            <DialogDescription>
              This action cannot be undone. Any applications using this key will
              immediately lose access.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRevokeDialog(null)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (showRevokeDialog) {
                  void handleRevoke(showRevokeDialog);
                }
              }}
              disabled={revokeApiKey.isPending}
            >
              {revokeApiKey.isPending ? 'Revoking...' : 'Revoke Key'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
